import React, { useMemo } from 'react';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { createSafeStylesAccessor } from '../helpers/cssHelpers';
import { getTextWidth } from 'components/common/size_helpers';
import {
  DEFAULT_FONT_SIZE,
  EDGE_PADDING,
  LABEL_POINT_GAP,
  POINT_RADIUS,
} from './pointLabelConstants';

function PointLabels({ series, xScale, yScale, innerWidth, innerHeight }) {
  const { get } = useReportConfiguration();
  const showLabels = get('showLabels');

  const safeStyles = createSafeStylesAccessor(get() || {});
  const dataLabelFormat = safeStyles.getDataLabelFormat();

  // Get font size from dataLabelFormat or use default
  const fontSize = parseInt(dataLabelFormat.fontSize) || DEFAULT_FONT_SIZE;
  const fontWeight = dataLabelFormat.fontWeight || 'normal';

  // Collect all points from all series with their scaled positions and measured label dimensions.
  // We measure each label's actual rendered width and height so we can intelligently position them
  // to prevent overflow, regardless of how many points, how long the labels are, or what font size is used.
  // Memoize this since getTextWidth is expensive (creates DOM elements to measure).
  const allPoints = useMemo(() => {
    if (!showLabels) return [];
    const points = [];
    series.forEach(line => {
      // Skip the fake null series (used to stabilize the x-axis)
      if (line.id === null) return;

      line.data.forEach(point => {
        if (point.data.y !== null && point.data.y !== undefined) {
          const label = String(point.data.yFormatted || point.data.y);
          const { width: labelWidth, height: labelHeight } = getTextWidth(
            label,
            fontSize,
            null,
            '',
            fontWeight
          );

          const scaledX = xScale(point.data.x);
          const scaledY = yScale(point.data.y);
          const halfWidth = labelWidth / 2;

          // Calculate text-anchor based on label width and position to prevent clipping
          let textAnchor = 'middle';
          if (scaledX - halfWidth < EDGE_PADDING) {
            textAnchor = 'start'; // Anchor to start, label extends rightward
          } else if (scaledX + halfWidth > innerWidth - EDGE_PADDING) {
            textAnchor = 'end'; // Anchor to end, label extends leftward
          }

          // Determine vertical position based on actual label height and chart boundaries
          // Position label above the point by default (from point's top edge + gap)
          const labelAboveY = scaledY - POINT_RADIUS - LABEL_POINT_GAP;
          const wouldOverflowTop = labelAboveY - labelHeight < EDGE_PADDING;

          // Check if positioning label below would overflow the bottom
          // (from point's bottom edge + gap)
          const labelBelowY = scaledY + POINT_RADIUS + LABEL_POINT_GAP;
          const wouldOverflowBottom =
            labelBelowY + labelHeight > innerHeight - EDGE_PADDING;

          // Position label below if it would overflow the top, above if it would overflow the bottom
          // Otherwise prefer above (default behavior)
          let labelY, dominantBaseline;
          if (wouldOverflowTop && !wouldOverflowBottom) {
            labelY = labelBelowY;
            dominantBaseline = 'hanging';
          } else {
            labelY = labelAboveY;
            dominantBaseline = 'auto';
          }

          points.push({
            serieId: line.id,
            x: scaledX,
            y: labelY,
            yFormatted: point.data.yFormatted || point.data.y,
            textAnchor,
            dominantBaseline,
          });
        }
      });
    });
    return points;
  }, [
    showLabels,
    series,
    xScale,
    yScale,
    fontSize,
    fontWeight,
    innerWidth,
    innerHeight,
  ]);

  if (!showLabels) return null;

  return (
    <g>
      {allPoints.map((point, index) => {
        // Add slight horizontal offset for edge-aligned labels for better visual appeal
        const labelStyle = {
          ...dataLabelFormat,
          fontSize: dataLabelFormat.fontSize || `${DEFAULT_FONT_SIZE}px`,
          fill: dataLabelFormat.color || 'var(--theme-primary-text)',
          pointerEvents: 'none',
        };

        // Offset by approximately half a character width. This is a minor tweak to make the text look
        // better aligned with the point when left- or right-aligned.
        if (point.textAnchor === 'start') {
          labelStyle.transform = 'translateX(-0.5ch)';
        } else if (point.textAnchor === 'end') {
          labelStyle.transform = 'translateX(0.5ch)';
        }

        return (
          <text
            key={`${point.serieId}-${index}`}
            x={point.x}
            y={point.y}
            textAnchor={point.textAnchor}
            dominantBaseline={point.dominantBaseline}
            style={labelStyle}
          >
            {point.yFormatted}
          </text>
        );
      })}
    </g>
  );
}

export default PointLabels;
